import autobind from 'class-autobind';
import saveFile from 'file-saver';
import merge from 'lodash/merge';
import noop from 'lodash/noop';
import { matchPath } from 'react-router';

import { NAVIGATOR_OFFLINE } from 'shared/constants/MessageTypes';
import {
  addSiteMessage,
  clearSelectedMenuItem,
  hideLoader,
  livePlayGame,
  showAlertDialog,
  showAtgBetConfigDialog,
  showConfirmDialog,
  showErrorDialog,
  showLoader,
} from 'shared/redux/actions';
import {
  settings as settingsSelectors,
  shares as shareSelectors,
  ui as uiSelectors,
  user as userSelectors,
} from 'shared/redux/selectors';
import { getStore } from 'shared/redux/store';
import { createShopUrl } from 'shared/utils';
import { getNow, toLocalMoment } from 'shared/utils/dateUtils';
import Sentry from 'shared/utils/sentry';
import { getText, hasText } from 'shared/utils/Text';

import BetConfig from './api/BetConfig';
import BetType from './api/BetType';
import BottomInfo from './components/BottomInfo';
import Dialog from './components/dialog/Dialog';
import ExportDialog from './components/dialog/ExportDialog';
import NorwayExportDialog from './components/dialog/NorwayExportDialog';
import ABCDReductionView from './components/grid/ABCDReductionView';
import BaseHorseReductionView from './components/grid/BaseHorseReductionView';
import DividendReductionView from './components/grid/DividendReductionView';
import FactorReductionView from './components/grid/FactorReductionView';
import Grid from './components/grid/Grid';
import JockeyReductionView from './components/grid/JockeyReductionView';
import OwnReductionView from './components/grid/OwnReductionView';
import PointReductionView from './components/grid/PointReductionView';
import PresetReductionView from './components/grid/PresetReductionView';
import RankingView from './components/grid/RankingView';
import StartNumberReductionView from './components/grid/StartNumberReductionView';
import TrainerReductionView from './components/grid/TrainerReductionView';
import TrendView from './components/grid/TrendView';
import {
  close as closeHorseInfoPopup,
  create as createHorseInfoPopup,
  updateData as updateHorseInfoPopup,
} from './components/horseinfo/HorseInfoPopup';
import {
  close as closeRaceAnalysisPopup,
  create as createRaceAnalysisPopup,
  updateData as updateRaceAnalysisPopup,
} from './components/raceanalysis/RaceAnalysisPopup';
import {
  DEFAULT,
  DIVIDEND_RECALC,
  DIVIDENDS,
  OWNWINPERCENTAGE_RECALC,
} from './constants/BetUpdateType';
import {
  BUY_HORSE_ANALYSIS_PRESSED,
  VIEW_HORSEINFO_RACE,
  VIEW_RACE_ANALYSIS,
} from './constants/Events';
import { TOP_INFO } from './constants/PopupMenuTypes';
import PopupMenu from './PopupMenu';
import { changeView, clearCurrentBet } from './redux/actions';
import observeStore from './redux/observeStore';
import { bet as betSelectors, view as viewSelectors } from './redux/selectors';
import EventDispatcher from './utils/EventDispatcher';
import { promiseRequest } from './utils/Request';
import RequestError from './utils/RequestError';
import { clone, formatDate } from './utils/Utils';

const $ = window.$;

class Joker {
  constructor(api, history) {
    autobind(this);
    this.api = api;
    this.history = history;
    this.setUpdateMenuFn();
    this.setUpdateTopInfoFn();
    this.viewHistory = {
      current: undefined,
      previous: undefined,
    };
    this.dispatch = getStore().dispatch;
    this.getState = getStore().getState;

    EventDispatcher.addListener(
      BUY_HORSE_ANALYSIS_PRESSED,
      this.onHorseInfoBuyHorseAnalysisPressed,
    );
    EventDispatcher.addListener(
      VIEW_HORSEINFO_RACE,
      this.onViewHorseInfoRacePressed,
    );
    EventDispatcher.addListener(VIEW_RACE_ANALYSIS, this.onRaceAnalysisPressed);
    this.destroyViewChangeObserver = observeStore(
      getStore(),
      this.onViewChange,
      viewSelectors.viewSelector,
    );
    this.destroyVisibilityChangedObserver = observeStore(
      getStore(),
      this.onVisibilityChanged,
      uiSelectors.isPageVisible,
    );
    this.destroySettingsChangeObserver = observeStore(
      getStore(),
      this.onSettingsChange,
      settingsSelectors.getSettings,
    );
  }

  destroy() {
    this.dispatch(hideLoader());
    this.dispatch(clearCurrentBet());
    EventDispatcher.removeAllListeners();
    this.destroyViewChangeObserver();
    this.destroyVisibilityChangedObserver();
    this.destroySettingsChangeObserver();
    closeHorseInfoPopup();
    closeRaceAnalysisPopup();
    this.grid && this.grid.destroy();
    $(window).unevent('resize');
  }

  setUpdateMenuFn(fn) {
    this.updateMenu = fn || noop;
  }

  setUpdateTopInfoFn(fn) {
    this.updateTopInfo = fn || noop;
  }

  setupUI(mainElement, middleElement, bottomElement) {
    this.$main = window.$(mainElement);
    this.grid = new Grid(this, this.$main, $(middleElement));
    this.bottomInfo = new BottomInfo(this, this.$main, $(bottomElement));
  }

  onInitialData() {
    this.updateMenu();
    this.updateTopInfo();
  }

  onSettingsChange() {
    this.grid && this.grid.updateAll();
    this.bottomInfo && this.bottomInfo.updateContent();
  }

  // ---------------------------------------- PAGE VISIBILTY ----------------------------------------------

  onVisibilityChanged(visible, prevVisible) {
    if (prevVisible == null) {
      return;
    }

    if (visible) {
      this.api.stompCheck();

      if (this.waitForReappearanceCallback) {
        this.waitForReappearanceCallback();
        this.waitForReappearanceCallback = null;
      }
    }
  }

  waitForReappearance(callback) {
    this.waitForReappearanceCallback = callback;
  }

  // ---------------------------------------- API DELEGATE METHODS ----------------------------------------

  onAPIRequestError(request) {
    const { error } = request;
    // TypeError from fetch likely means a network error
    if (!window.navigator.onLine || error instanceof TypeError) {
      this.dispatch(addSiteMessage(NAVIGATOR_OFFLINE));
    } else if (error.getErrorType() === 'discrete') {
      this.updateTopInfo({
        indicator: 'warning',
        betDataInvalid: true,
        error,
      });
    } else {
      this.dispatch(
        showErrorDialog({
          message: error.getMessage(),
          details: error.getDetails(),
          logNumber: error.getLogNumber(),
        }),
      );
    }
  }

  onAPIUpdateScheduled() {
    this.updateTopInfo({
      indicator: 'spinner',
      betDataInvalid: true,
    });
    this.updateMenu({
      betUpdateActive: true,
    });
  }

  onAPIUpdateStarted() {
    this.updateTopInfo({
      indicator: 'spinner',
      betDataInvalid: true,
    });
    this.updateMenu({
      betUpdateActive: true,
    });
  }

  onAPIUpdateIgnored() {
    this.updateTopInfo({
      indicator: null,
      betDataInvalid: false,
    });
    this.updateMenu({
      betUpdateActive: false,
    });
  }

  onAPIUpdateComplete(error) {
    const stake = this.api.getStake();
    const noRowsCreated =
      stake.getUnreducedRows() > 1 && stake.getReducedRows() === 0;
    const horsesWithoutMarks = stake.getHorsesWithoutMarks();
    const userMessage = stake.getUserMessage();

    const errors = [];
    if (noRowsCreated) {
      errors.push(
        new RequestError({
          messageId: 'warning-no_playable_rows',
        }),
      );
    }
    if (horsesWithoutMarks && !horsesWithoutMarks.isEmpty()) {
      errors.push(
        new RequestError({
          messageId: 'warning-system_contains_horses_without_marks',
          errorData: horsesWithoutMarks,
        }),
      );
    }
    if (userMessage) {
      errors.push(
        new RequestError({
          message: userMessage,
        }),
      );
    }
    if (error) {
      errors.push(error);
    }

    if (errors.length > 0) {
      this.updateTopInfo({
        indicator: 'warning',
        betDataInvalid: !!error,
        error: errors,
      });
    } else {
      this.updateTopInfo({
        indicator: null,
        betDataInvalid: false,
        error: null,
      });
    }

    this.updateMenu({
      betUpdateActive: false,
      betHasCoupons: stake.getNumberOfCoupons() > 0,
    });

    this.bottomInfo && this.bottomInfo.updateContent();
  }

  onAPILoadedBetInformation(success) {
    if (success) {
      this.grid && this.grid.updateAll();
      this.updateTopInfo();
      this.updateMenu();
    }
  }

  onAPILoadedBetRow(row) {
    this.grid && this.grid.updateRow(row);
  }

  onAPIUpdatedGameRow(row) {
    this.grid && this.grid.updateRow(row);
  }

  onAPIUpdatedGameInformation() {
    this.bottomInfo && this.bottomInfo.updateInfo();
    this.bottomInfo && this.bottomInfo.updateContent();
    this.updateTopInfo();
  }

  onAPIUpdatedResultRow(row) {
    this.grid && this.grid.updateRow(row);
  }

  onAPIUpdatedResultInformation() {
    this.updateTopInfo();
    this.bottomInfo && this.bottomInfo.updateContent();
  }

  onAPIUpdatedMyDividends() {
    this.bottomInfo && this.bottomInfo.updateContent();
  }

  onAPIUpdatedRowsLeftInformation() {
    this.grid && this.grid.updateAll();
    this.bottomInfo && this.bottomInfo.updateContent();
  }

  onAPIUpdatedRaceInformation() {
    this.grid && this.grid.updateAll();
    this.updateTopInfo();
    this.bottomInfo && this.bottomInfo.updateAll();
  }

  onAPIUpdatedJockeyInformation() {
    this.grid && this.grid.updateAll();
  }

  onAPIStakeRowUpdated(row) {
    this.grid && this.grid.updateRow(row);
  }

  onResetPointSource({ pointIndex }) {
    const { data } = this.api.betInformation;

    if (data.pointSource && data.pointSource[pointIndex]) {
      delete data.pointSource[pointIndex];
      this.api.setBetInformation(data);
    }
  }

  onSetPointSource({ pointSource, pointIndex }) {
    // alternative: this.api.betInformation.data.pointSource = pointSource;
    const { data } = this.api.betInformation;

    this.api.setBetInformation({
      ...data,
      pointSource: {
        ...data.pointSource,
        [pointIndex]: pointSource,
      },
    });
  }

  // Rank point display
  setPointDisplay(pointSource, pointIndex) {
    let i, j;
    const rows = this.api.betInformation.getRows();

    if (pointSource === 'reset') {
      for (i = 0; i < rows.length; ++i) {
        const displayRow = rows[i];
        for (j = 0; j < displayRow.data.order.length; ++j) {
          displayRow.setPointReductionPoints(j, pointIndex, j + 1);
        }
      }
      this.api.startBetUpdate(this.onResetPointSource({ pointIndex }));
      this.grid && this.grid.updateAll();
    } else {
      this.api.setPointsFromSource(pointSource, pointIndex, result => {
        if (
          result.length === 0 &&
          [
            'pointAsInsats30m',
            'pointAsInsats1t',
            'pointAsInsats2t',
            'pointAsOwnWin',
          ].includes(pointSource)
        ) {
          const [header, body] =
            pointSource === 'pointAsOwnWin'
              ? [
                  getText('dialog-copy-own-evaluation-empty'),
                  getText('dialog-copy-own-evaluation-empty-message'),
                ]
              : [
                  getText('dialog-copy-trend-empty'),
                  getText('dialog-copy-trend-empty-message'),
                ];
          this.emptyCopyData(header, body);
          return;
        }

        for (i = 0; i < rows.length; ++i) {
          const valueRow = result[i];
          const displayRow = rows[i];
          for (j = 0; j < displayRow.data.order.length; ++j) {
            displayRow.setPointReductionPoints(j, pointIndex, valueRow[j]);
          }
        }
        this.api.startBetUpdate(
          this.onSetPointSource({ pointSource, pointIndex }),
        );
        this.grid && this.grid.updateAll();
      });
    }
  }

  emptyCopyData(title, message) {
    this.dispatch(
      showAlertDialog({
        title: title,
        details: message,
      }),
    );
  }

  fetchInitialDividends = viewCallback =>
    this.api.fetchDividends(() => {
      this.grid && this.grid.updateAll();
      viewCallback();
    });

  onClaimStatusChanged = () => {
    this.bottomInfo?.updateLockedState();
  };

  // ---------------------------------------- MENU DELEGATE METHODS ----------------------------------------

  onMenuExportPressed = () => {
    // Users who have been invited to the bet are only allowed to export it if
    // it has been locked by the creator first
    const isLocked = this.api.betInformation.getBetConfig().isLocked();
    const isOwner = shareSelectors.isOwner(this.getState(), {
      type: 'atg',
      betId: betSelectors.getCurrentBetId(this.getState()),
    });
    if (!isLocked && !isOwner) {
      this.dispatch(clearSelectedMenuItem());
      this.dispatch(
        showAlertDialog({
          title: getText('blocked-export-headline'),
          details: getText('blocked-export-body'),
        }),
      );
      return;
    }

    let exportApiCall = this.api.exportXML;
    let exportDialogClass = ExportDialog;
    if (IS_NO_SITE) {
      exportApiCall = this.api.uploadToRikstoto;
      exportDialogClass = NorwayExportDialog;
    }
    exportApiCall(responseData => {
      if (responseData && responseData.wasLocked) {
        const betConfig = this.api.betInformation.getBetConfig();

        betConfig.setLockTime(responseData.lockTime);
        betConfig.setLocked(true);
        this.grid && this.grid.updateAll();
        this.updateTopInfo();
        this.bottomInfo && this.bottomInfo.updateContent();
      }

      new exportDialogClass(this, responseData, async ok => {
        this.dispatch(clearSelectedMenuItem());
        const state = this.getState();
        const userAlias = userSelectors.getAlias(state);
        const betId = betSelectors.getCurrentBetId(state);
        const liveParticipant = userSelectors.isLiveParticipant(state);

        const firstRace = this.api.raceInformation.getRace(0);
        const { postTime } = firstRace.data;
        const isNotEnded = getNow().isBefore(toLocalMoment(postTime));

        if (liveParticipant && userAlias && ok && isNotEnded) {
          this.dispatch(
            showAlertDialog({
              title: getText('dialog-add-to-live-title'),
              details: getText('dialog-add-to-live-details'),
            }),
          );

          new Promise((_resolve, reject) =>
            this.dispatch(livePlayGame({ betId, type: 'atg', reject })),
          ).catch(error =>
            console.error(`${error.status}: ${error.statusText}`),
          );
        }

        if (ok === 2) {
          try {
            const response = await promiseRequest({
              path: `game/fetchXML?betId=${betId}`,
              headers: {
                Accept: 'application/xml',
                'Content-Type': 'application/xml',
              },
            });
            const blob = await response.blob();
            const h = response.headers.get('Content-Disposition') || '';
            const filename = h.slice(
              h.indexOf('filename=') + 'filename='.length,
            );
            saveFile(blob, filename);

            // On Norwegian site, systems are only downloaded for shared bets.
            // Therefore we ask the user to navigate to Rikstoto upon downloading the system.
            if (IS_NO_SITE) {
              this.dispatch(
                showConfirmDialog({
                  title: getText('dialog-export-rikstoto-title'),
                  details: getText(
                    'dialog-export-rikstoto-description',
                    filename,
                  ),
                  positive: getText('dialog-export-rikstoto-button-lag'),
                  url: 'https://lag.rikstoto.no',
                  secondaryOptions: [
                    {
                      caption: getText(
                        'dialog-export-rikstoto-button-andelstorg',
                      ),
                      url: 'https://kommweb.rikstoto.no/',
                    },
                  ],
                  negative: getText('dialog-export-button-cancel'),
                }),
              );
            }
          } catch (error) {
            Sentry.captureException(error);
            console.error(error);
          }
        } else if (ok === 3) {
          try {
            const response = await promiseRequest({
              path: `game/fetchNR?betId=${betId}`,
              headers: {
                Accept: 'application/xml',
                'Content-Type': 'application/xml',
              },
            });
            const blob = await response.blob();
            const h = response.headers.get('Content-Disposition') || '';
            const filename = h.slice(
              h.indexOf('filename=') + 'filename='.length,
            );
            saveFile(blob, filename);
          } catch (error) {
            Sentry.captureException(error);
            console.error(error);
          }
        }
      });
    });
  };

  onMenuRecalcBetPressed = () => this.api.recalcBet();
  getExporterAPI = () => this.api;

  // ---------------------------------------- DIALOGS ----------------------------------------

  removeAtgDialogs = () => Dialog.closeAll(true);
  getDialogAPI = () => this.api;

  showAlertWhenLocked = conditions =>
    viewSelectors
      .getCurrentView(this.getState())
      ?.showAlertWhenLocked(conditions);

  // ---------------------------------------- ARCHIVE ----------------------------------------

  openArchivedBet(savedBet) {
    this.history.push(`/trav/${savedBet.getID()}`);
  }

  duplicateArchivedBet(savedBet, callback) {
    this.dispatch(showLoader('main-loader-duplicate-bet-heading'));
    this.api.duplicateSavedBet(savedBet, betId => {
      if (betId) {
        this.history.push(`/trav/${betId}`, { showBetConfig: true });
      } else {
        this.dispatch(hideLoader());
      }
      if (callback != null) {
        callback();
      }
    });
  }

  // ---------------------------------------- VIEW DELEGATE METHODS ----------------------------------------

  /**
   * Show horse info popup for a race
   * @param  {Number} row         race row
   * @param  {Number} startNumber optional start number to focus
   */
  onViewHorseInfoRacePressed(row, startNumber) {
    this.grid && this.grid.closeHighlight();

    const race = this.api.raceInformation.getRace(row);
    const legNumber = race.getLegNumber();

    const props = {
      numberOfStarts: race.getNumberOfStarts(),
      raceIndex: row,
      startNumber: startNumber,
      raceDetails: race.getRaceDetails(),
    };

    createHorseInfoPopup(this.api, this.$main, props);

    this.api.loadHorseSetInformation(legNumber, function (horseInfoSet) {
      if (horseInfoSet) {
        updateHorseInfoPopup(
          merge(props, {
            dataSet: horseInfoSet,
          }),
        );
      } else {
        closeHorseInfoPopup();
      }
    });
  }

  /**
   * Show horse info popup for a single horse
   * @param  {Ojbect} position         grid row and column
   */
  onViewHorseInfoSinglePressed(position) {
    this.grid && this.grid.closeHighlight();

    const race = this.api.raceInformation.getRace(position.row);
    const legNumber = race.getLegNumber();
    const startNumber = this.api.betInformation
      .getRow(position.row)
      .getStartNumberAtPosition(position.column);

    const props = {
      numberOfStarts: race.getNumberOfStarts(),
      raceIndex: position.row,
      startNumber: startNumber,
      raceDetails: race.getRaceDetails(),
      isSingleView: true,
    };

    createHorseInfoPopup(this.api, this.$main, props);

    this.api.loadHorseInformation(
      legNumber,
      startNumber,
      function (horseInfoSet) {
        if (horseInfoSet) {
          updateHorseInfoPopup(
            merge(props, {
              dataSet: horseInfoSet,
            }),
          );
        } else {
          closeHorseInfoPopup();
        }
      },
    );
  }

  onTodaysBestBetPressed(position) {
    const betKey = this.api.betInformation.getBetKey();
    this.api.loadBetKeyInfo(betKey, betKeyInfo => {
      const race = this.api.raceInformation.getRace(position.row);
      const raceNumber = race.getRaceNumber();
      const startNumber = this.api.betInformation
        .getRow(position.row)
        .getStartNumberAtPosition(position.column);
      const rikstotoTrackCode = betKeyInfo.getRikstotoTrackCode();
      const trackName = betKeyInfo.getTrack().getName();
      const trackInfo = `${trackName} (${rikstotoTrackCode})`;

      const startIndex = startNumber - 1;
      const start = race.getStart(startIndex);
      let horseInfo = startNumber;
      if (start) {
        const horse = start.getHorse();
        if (horse) {
          horseInfo += `. ${horse.getName()}`;
        }
      }
      const summary = `Spelform: Vinner<br>Datum: ${formatDate(
        betKey.getDate(),
      )}<br>Bana: ${trackInfo}<br>Lopp: ${raceNumber}<br>Häst: ${horseInfo}`;

      this.dispatch(
        showConfirmDialog({
          onConfirm: () => {
            this.api.setTodaysBestBet(
              {
                betKey,
                raceNumber,
                startNumber,
                rikstotoTrackCode,
              },
              () => {
                this.dispatch(
                  showAlertDialog({
                    title: 'Dagens Bästa Spel sparat!',
                    details: `Följande spel har sparats: <br><br>${summary}`,
                  }),
                );
              },
            );
          },
          title: 'Publicera som Dagens Bästa Spel?',
          details: `Dagens Bästa Spel kommer att synas högst upp på Jokersystemets startsida för alla besökare.<br><br>Sammanfattning:<br>${summary}`,
          positive: 'Ja, publicera!',
          negative: 'Avbryt',
        }),
      );
    });
  }

  onDeleteTodaysBestBetPressed() {
    const betKey = this.api.betInformation.getBetKey();
    const date = formatDate(betKey.getDate());
    this.dispatch(
      showConfirmDialog({
        onConfirm: () => {
          this.api.deleteTodaysBestBet(betKey.getDate().getTime(), () => {
            this.dispatch(
              showAlertDialog({
                title: 'Dagens Bästa Spel raderat!',
                details: `Dagens Bästa Spel har raderats för datumet ${date}.`,
              }),
            );
          });
        },
        title: 'Radera Dagens Bästa Spel?',
        details: `Dagens Bästa Spel kommer inte längre att synas högst upp på Jokersystemets startsida följande dag: ${date}`,
        positive: 'Ja, radera!',
        negative: 'Avbryt',
      }),
    );
  }

  // ---------------------------------------- HORSE INFO EVENT LISTENERS ----------------------------------------

  onHorseInfoBuyHorseAnalysisPressed(callbackOptions) {
    this.dispatch(
      showConfirmDialog({
        onConfirm: () => {
          this.waitForReappearance(() => {
            this.onViewHorseInfoRacePressed(
              callbackOptions.row,
              callbackOptions.startNumber,
            );
          });
        },
        title: getText('dialog-buy-horse-analysis-title'),
        details: getText(
          'dialog-buy-horse-analysis-message',
          this.api.betInformation.getBetKey().getBetType(),
        ),
        positive: getText('dialog-buy-horse-analysis-positive'),
        negative: getText('dialog-buy-horse-analysis-negative'),
        url: createShopUrl(callbackOptions.sku),
      }),
    );
  }

  // ---------------------------------------- TOP INFO DELEGATE METHODS ----------------------------------------

  isSharedBetActive() {
    return (
      this.api.betInformation &&
      this.api.betInformation.getBetConfig().isShared()
    );
  }

  getCurrentReductionViewMenuTitle() {
    const currentView = viewSelectors.getCurrentView(this.getState());
    return currentView ? currentView.getMenuTitle() : '';
  }

  onTopInfoMenuPressed(position) {
    if (this.topInfoPopupMenu) {
      this.closeTopInfoPopupMenu();
    } else {
      const items = this.getTopInfoPopupMenuItems();
      this.topInfoPopupMenu = new PopupMenu(
        TOP_INFO,
        position,
        items,
        this.closeTopInfoPopupMenu,
      );
      this.updateTopInfo({ reductionMenuVisible: true });
    }
  }

  isOnlyAllowingABCD() {
    return (
      ALLOW_FREEMIUM_HORSE_BETTING &&
      !this.isSharedBetActive() &&
      !userSelectors.hasSubscription(this.getState()) &&
      !viewSelectors.getCurrentView(this.getState())?.isCollaborativeBet()
    );
  }

  // TODO - Creating all the reduction views every time this is called is very wasteful, should be refactored
  getTopInfoPopupMenuItems() {
    const gameInformation = this.api.gameInformation;
    const betInformation = this.api.betInformation;
    const betConfig = betInformation.getBetConfig();
    const betKey = betInformation.getBetKey();
    const betTypeCode = betKey && betKey.getBetType();
    // In Norway, users without subscription will still be able to enter the
    // horse racing Joker system, but they will only be able to access ABCD mode.
    const allowOnlyABCD = this.isOnlyAllowingABCD();

    let betType, views, set, numberOfReductions, n;

    if (betKey) {
      betType = BetType.getBetType(betTypeCode);
    }

    const reductionSet = betInformation.getReductionSet();
    const items = [];

    items.push(
      this.getTopInfoPopupMenuItemForView(new RankingView(this)),
      '--------------------',
    );

    if (
      IS_SE_SITE &&
      !['LD', 'DD', 'V3', 'T'].includes(betTypeCode) &&
      !betInformation.isGallop()
    ) {
      const presetReductionMenuItem = this.getTopInfoPopupMenuItemForView(
        new PresetReductionView(this),
      );
      presetReductionMenuItem.locked = allowOnlyABCD;
      items.push(presetReductionMenuItem);
    }

    if (reductionSet.getABCDReduction()) {
      items.push(
        this.getTopInfoPopupMenuItemForView(new ABCDReductionView(this)),
      );
    }

    views = [];
    set = reductionSet.getPointReductionSet();

    if (set) {
      numberOfReductions = set.getNumberOfPointReductions();

      for (n = 0; n < numberOfReductions; n++) {
        views.push(new PointReductionView(this, n));
      }

      const pointReductionMenuItem =
        this.getTopInfoPopupMenuItemGroupForViews(views);
      if (pointReductionMenuItem) {
        pointReductionMenuItem.locked =
          allowOnlyABCD && !betConfig.isFromJokerWiz();
        items.push(pointReductionMenuItem);
      }
    }

    views = [];
    set = reductionSet.getBaseHorseReductionSet();

    if (set) {
      numberOfReductions = set.getNumberOfBaseHorseReductions();

      for (n = 0; n < numberOfReductions; n++) {
        views.push(new BaseHorseReductionView(this, n));
      }

      const baseReductionMenuItem =
        this.getTopInfoPopupMenuItemGroupForViews(views);
      if (baseReductionMenuItem) {
        baseReductionMenuItem.locked = allowOnlyABCD;
        items.push(baseReductionMenuItem);
      }
    }

    items.push('--------------------');

    if (reductionSet.getOwnReduction() && betType.hasOwnEvaluationReduction()) {
      const ownReductionViewInstance = new OwnReductionView(this);

      this.onResetOwnWinPercentages =
        ownReductionViewInstance.resetOwnWinPercentages;
      const ownReductionMenuItem = this.getTopInfoPopupMenuItemForView(
        ownReductionViewInstance,
      );
      ownReductionMenuItem.locked = allowOnlyABCD;
      items.push(ownReductionMenuItem);
    }

    if (betType.hasTrendReduction()) {
      const trendViewInstance = new TrendView(this);

      this.onChangeTrendTime = trendViewInstance.changeTrendTime;
      const trendReductionMenuItem =
        this.getTopInfoPopupMenuItemForView(trendViewInstance);
      trendReductionMenuItem.locked = allowOnlyABCD;
      items.push(trendReductionMenuItem);
    }

    if (reductionSet.getStartNumberReduction()) {
      const startNumberReductionMenuItem = this.getTopInfoPopupMenuItemForView(
        new StartNumberReductionView(this),
      );
      startNumberReductionMenuItem.locked = allowOnlyABCD;
      items.push(startNumberReductionMenuItem);
    }

    if (
      betType &&
      betType.hasJockeyTrainerReduction() &&
      reductionSet.getJockeyReduction()
    ) {
      const jockeyReductionMenuItem = this.getTopInfoPopupMenuItemForView(
        new JockeyReductionView(this),
      );
      jockeyReductionMenuItem.locked = allowOnlyABCD;
      items.push(jockeyReductionMenuItem);
    }

    if (
      betType &&
      betType.hasJockeyTrainerReduction() &&
      reductionSet.getTrainerReduction()
    ) {
      const trainerReductionMenuItem = this.getTopInfoPopupMenuItemForView(
        new TrainerReductionView(this),
      );
      trainerReductionMenuItem.locked = allowOnlyABCD;
      items.push(trainerReductionMenuItem);
    }

    const dividendReductionUnavailableDueToMissingOdds =
      ['T', 'DD', 'LD'].includes(betTypeCode) && !gameInformation.hasOdds();
    if (
      betType &&
      betType.hasDividendReduction() &&
      !dividendReductionUnavailableDueToMissingOdds &&
      reductionSet.getDividendReduction()
    ) {
      const dividendReductionMenuItem = this.getTopInfoPopupMenuItemForView(
        new DividendReductionView(this),
      );
      dividendReductionMenuItem.locked = allowOnlyABCD;
      items.push(dividendReductionMenuItem);
    }

    if (reductionSet.getFactorReduction()) {
      const numberOfErrorsGuarantee = betConfig.getNumberOfErrorsGuarantee();
      const factorReductionViewInstance = new FactorReductionView(this);

      // disable "factor" item if guarantee selected

      if (numberOfErrorsGuarantee > 0) {
        factorReductionViewInstance.setIsDisabled(true);
      }

      const factorReductionMenuItem = this.getTopInfoPopupMenuItemForView(
        factorReductionViewInstance,
      );
      factorReductionMenuItem.locked = allowOnlyABCD;
      items.push(factorReductionMenuItem);
    }

    return items;
  }

  getTopInfoPopupMenuItemForView(view) {
    return {
      view,
      title: view.getMenuTitle(),
      selected: view.getMenuSelected(),
      togglable: view.getMenuTogglable(),
      toggled: view.getMenuToggled(),
      toggleEnabled: !this.isSharedBetActive(),
      deletable: view.getMenuDeletable(),
      deleteEnabled: !this.isSharedBetActive(),
      disabled: view.getIsDisabled(),
      onPress: () => {
        this.onTopInfoPopupMenuViewPressed(view);
      },
      onUpgradePress: () => {
        this.onUpgradeMenuItemPressed();
      },
      onToggle: () => {
        this.onTopInfoPopupMenuToggled(view);
      },
      onDelete: () => {
        this.onTopInfoPopupMenuDeleted(view);
      },
    };
  }

  updateTopInfoPopupMenu() {
    if (this.topInfoPopupMenu) {
      this.topInfoPopupMenu.setItems(this.getTopInfoPopupMenuItems());
    }
  }

  onTopInfoPopupMenuAddViewItemPressed(view) {
    view.addItem();
    const numberOfItems = view.getNumberOfItems();
    const ViewClass = view.constructor;
    const newView = new ViewClass(this, numberOfItems - 1);
    this.onChangeView(newView, view.getGridClass(), newView.hasToolbar());
    this.closeTopInfoPopupMenu();
  }

  onTopInfoPopupMenuToggled(view) {
    view.toggleItem();

    if (view instanceof PresetReductionView) {
      // UI of PresetReductionView depends on toggle, so if it is currently
      // showing behind the menu, update it
      if (view.isEqualToView(viewSelectors.getCurrentView(this.getState()))) {
        this.onChangeView(view, view.getGridClass(), view.hasToolbar());
      }
      // Selection marker of PresetReduction is affected by its toggle state
      // and needs to update immediately
      this.updateTopInfoPopupMenu();
    }
  }

  onTopInfoPopupMenuDeleted(view) {
    view.deleteItem();

    const currentView = viewSelectors.getCurrentView(this.getState());
    const indexPath = this.topInfoPopupMenu.getIndexPathForItem(
      function (item) {
        if (item.view) {
          return item.view.isEqualToView(currentView);
        }
      },
    );

    this.updateTopInfoPopupMenu();

    let item;

    // TOOO - Refactor
    // eslint-disable-next-line no-constant-condition
    while (true) {
      item = this.topInfoPopupMenu.getItemForIndexPath(indexPath);

      if (!item) {
        // eslint-disable-next-line no-param-reassign
        view = new RankingView(this);
        break;
      }

      if (item.view) {
        // eslint-disable-next-line no-param-reassign
        view = item.view;
        break;
      }

      indexPath[indexPath.length - 1]--;
    }

    this.onChangeView(view, view.getGridClass(), view.hasToolbar());
  }

  onUpgradeMenuItemPressed() {
    const state = this.getState();
    const betId = betSelectors.getCurrentBetId(state);
    this.history.push(`/trav/${betId}/uppgradera`);
  }

  cancelUpgrade() {
    const isOnUpgradePath =
      matchPath(window.location.pathname, {
        path: '/trav/:betId/uppgradera',
      })?.isExact === true;
    if (isOnUpgradePath) {
      const state = this.getState();
      const betId = betSelectors.getCurrentBetId(state);
      this.history.push(`/trav/${betId}`);
    }
  }

  onTopInfoPopupMenuViewPressed(view) {
    this.cancelUpgrade();

    const canChange = this.viewHistory.current.canChangeView();

    if (canChange.ok) {
      if (view !== this.viewHistory.current) {
        this.viewHistory.previous = this.viewHistory.current;
        this.viewHistory.current = view;
      }
      this.onChangeView(view, view.getGridClass(), view.hasToolbar());
      this.closeTopInfoPopupMenu();
    } else {
      this.dispatch(
        showConfirmDialog({
          onConfirm: () => {
            if (view !== this.viewHistory.current) {
              this.viewHistory.previous = this.viewHistory.current;
              this.viewHistory.current = view;
            }
            this.onChangeView(view, view.getGridClass(), view.hasToolbar());
            this.closeTopInfoPopupMenu();
          },
          onReject: () => {
            this.closeTopInfoPopupMenu();
          },
          title: canChange.reason.title,
          details: canChange.reason.details,
          positive: getText('dialog-confirm-own-reduction-positive'),
          negative: getText('dialog-confirm-own-reduction-negative'),
        }),
      );
    }
  }

  onChangeView = (view, gridClass, hasToolbar) =>
    this.dispatch(changeView(view, gridClass, hasToolbar));

  getTopInfoPopupMenuItemGroupForViews(views) {
    if (views.length === 0) {
      return;
    }

    const firstView = views[0];

    const itemGroup = {
      items: [],
      title: firstView.getMenuGroupTitle(),
      addItemTitle: firstView.getMenuAddItemTitle(),
      onAddItem: () => {
        this.onTopInfoPopupMenuAddViewItemPressed(firstView);
      },
      onUpgradePress: () => {
        this.onUpgradeMenuItemPressed();
      },
    };

    for (const view of views) {
      itemGroup.items.push(this.getTopInfoPopupMenuItemForView(view));

      if (view.getMenuSelected()) {
        itemGroup.selected = true;
      }
    }

    const maxViews = firstView.getMenuMaxNumberOfViews();

    if (views.length < maxViews && !this.isSharedBetActive()) {
      itemGroup.items.push('--------', {
        title: itemGroup.addItemTitle,
        addable: true,
        color: 'blue',
        onPress: itemGroup.onAddItem,
      });
    }

    return itemGroup;
  }

  closeTopInfoPopupMenu() {
    PopupMenu.closeCurrent();
    this.updateTopInfo({ reductionMenuVisible: false });
    this.topInfoPopupMenu = null;
  }

  openBetConfigDialog() {
    const betInformation = this.api.betInformation;
    if (!betInformation) {
      return;
    }
    const betKey = betInformation.getBetKey();
    const oldBetConfig = betInformation.getBetConfig();
    if (!betKey || !oldBetConfig) {
      return;
    }
    const isUsingFactorReduction =
      Array.from({ length: betInformation.getNumberOfRows() }).some((_, idx) =>
        betInformation.getRow(idx).hasAnyFactors(),
      ) ||
      betInformation
        .getReductionSet()
        ?.getPointReductionSet()
        ?.hasAnyFactorsOnPointSums();

    this.api.loadBetKeyInfo(betKey, betKeyInfo => {
      this.dispatch(
        showAtgBetConfigDialog({
          betKey,
          betKeyInfo,
          betConfig: oldBetConfig,
          isUsingFactorReduction,
          isNewGame: false,
          onConfirmNewConfig: (newBetConfig, closeCallback) => {
            this.updateBetConfig(newBetConfig, oldBetConfig);
            closeCallback();
          },
        }),
      );
    });
  }

  onTopInfoLockChange(locked) {
    const isOwner = shareSelectors.isOwner(this.getState(), {
      type: 'atg',
      betId: betSelectors.getCurrentBetId(this.getState()),
    });
    if (!isOwner) {
      this.dispatch(
        showAlertDialog({
          title: getText('blocked-lock-headline'),
          details: getText('blocked-lock-body'),
        }),
      );
      return;
    }
    const { betInformation } = this.api;
    const oldBetConfig = betInformation.getBetConfig();
    if (oldBetConfig.isFromJokerWiz() && this.isOnlyAllowingABCD()) {
      this.dispatch(
        showConfirmDialog({
          onConfirm: () => {
            this.onUpgradeMenuItemPressed();
          },
          title: getText('blocked-lock-headline'),
          details: getText('blocked-wiz-lock-body'),
          positive: getText('missing-subscription-upgrade-button'),
        }),
      );
      return;
    }
    const betConfig = new BetConfig(clone(oldBetConfig.data));
    betConfig.setLocked(locked);
    this.updateBetConfig(betConfig, oldBetConfig);
  }

  updateBetConfig(newBetConfig, oldBetConfig) {
    if (this.showAlertWhenLocked({ excludedStates: ['LOCKED'] })) {
      return;
    }
    const { betInformation } = this.api;
    betInformation.setBetConfig(newBetConfig);

    this.grid && this.grid.updateAll();
    this.updateTopInfo();

    const currentView = viewSelectors.getCurrentView(this.getState());
    const betUpdateType =
      currentView instanceof DividendReductionView ? DIVIDENDS : DEFAULT;

    this.api.startBetUpdate(ok => {
      if (!ok) {
        betInformation.setBetConfig(oldBetConfig);
        betInformation.getBetConfig().validate();
        this.updateTopInfo();
      } else if (currentView instanceof PointReductionView) {
        currentView.handleBetConfigUpdate();
      }
    }, betUpdateType);
  }

  onTopInfoPopupMenuClosed() {
    this.topInfoPopupMenu = null;
    this.updateTopInfo();
  }

  onTopInfoCloseCalendar() {
    const view = new RankingView(this);
    this.onChangeView(view, view.getGridClass(), view.hasToolbar());
  }

  // ---------------------------------------- VIEWS ----------------------------------------

  onViewChange(state) {
    if (!state.currentView) {
      return;
    }

    if (this.viewHistory.current == null) {
      this.viewHistory.current = state.currentView;
    }

    if (this.grid) {
      this.grid.delegate = state.currentView;
      this.grid.updateAll();
    }

    if (this.bottomInfo) {
      this.bottomInfo.delegate = state.currentView;
      this.bottomInfo.updateAll();
    }
  }

  getViewGrid() {
    return this.grid;
  }

  getViewAPI() {
    return this.api;
  }

  getViewBottomInfo() {
    return this.bottomInfo;
  }

  onRaceAnalysisPressed(rowIndex) {
    const races = this.api.raceInformation;
    const race = races.getRace(rowIndex);
    const legNumber = race.getLegNumber();
    const raceKey = race.getRaceKey();

    const props = {
      raceNumber: rowIndex,
      numberOfRaces: races.getNumberOfRaces(),
      betType: this.getRaceAnalysisBetType(),
    };

    createRaceAnalysisPopup(this.api, this.$main, props);

    this.api.loadRaceAnalysis(
      legNumber,
      raceKey,
      function (raceAnalysis, errorMessage) {
        if (raceAnalysis && raceAnalysis.isAvailable()) {
          if (raceAnalysis.isBought()) {
            updateRaceAnalysisPopup(
              merge(props, {
                dataSet: raceAnalysis,
              }),
            );
          } else {
            closeRaceAnalysisPopup();
            this.dispatch(
              showConfirmDialog({
                onConfirm: () => {
                  this.waitForReappearance(() => {
                    this.onRaceAnalysisPressed(rowIndex);
                  });
                },
                title: getText('dialog-buy-race-analysis-title'),
                details: getText(
                  'dialog-buy-race-analysis-message',
                  this.api.betInformation.getBetKey().getBetType(),
                ),
                positive: getText('dialog-buy-race-analysis-positive'),
                negative: getText('dialog-buy-race-analysis-negative'),
                url: createShopUrl(raceAnalysis.getProductId()),
              }),
            );
          }
        } else {
          this.dispatch(
            showAlertDialog({
              title: getText('dialog-no-race-analysis-title'),
              details: errorMessage
                ? errorMessage
                : getText('dialog-no-race-analysis-message'),
            }),
          );
          closeRaceAnalysisPopup();
        }
      }.bind(this),
    );

    this.getRaceAnalysisHeading(function (header) {
      updateRaceAnalysisPopup({
        header: header,
      });
    });
  }

  onResetBetRankingAllRowsPressed(ranking) {
    if (this.showAlertWhenLocked()) {
      return;
    }
    this.api.resetBetRankingAllRows(ranking);
  }

  updateDividendCalculation() {
    this.api.startBetUpdate(null, DIVIDEND_RECALC);
  }

  updateWinPercentageCalculation() {
    this.api.startBetUpdate(null, OWNWINPERCENTAGE_RECALC);
  }

  // ---------------------------------------- RACE ANALYSIS DELEGATE ----------------------------------------

  getRaceAnalysisBetType() {
    const betType = this.api.raceInformation.getBetType();
    if (betType) {
      const betTypeTextID = `bet-type-short_${betType.toLowerCase()}`;
      if (hasText(betTypeTextID)) {
        return getText(betTypeTextID);
      }
    }
    return '';
  }

  getRaceAnalysisHeading(callback) {
    const betKey = this.api.betInformation.getBetKey();
    this.api.loadBetKeyInfo(betKey, function (betKeyInfo) {
      const parts = [];

      const track = betKeyInfo.getTrack();
      if (track) {
        parts.push(track.getName());
      }

      const time = betKeyInfo.getTime();
      if (time) {
        parts.push(formatDate(time));
      }

      callback(parts.join(', '));
    });
  }

  fetchCheckJockeyReductions(callback) {
    return this.api.checkJockeyReductions(callback);
  }
}

let instance = null;

export const getInstance = () => instance;

export const setup = (api, history) => {
  instance = new Joker(api, history);
  return instance;
};

export const teardown = () => {
  if (instance) {
    instance.destroy();
    instance = null;
  }
};
